/**
 * Helpers for the IPO Reviews page: consensus verdicts and the verdict filter they fall
 * under, Capital Market (CM) score bands, board tabs, table filters, summary stats and
 * CSV export.
 */

// bucket: the "Filter Verdict" chip a consensus counts towards. Neutral and high-risk calls
// count as "May Apply" because they are neither a clear apply nor an avoid.
// strength: ranks apply-side calls when choosing the top consensus pick.
export const CONSENSUS = {
  strong_apply: { label: 'Strong Apply', bucket: 'apply', strength: 3 },
  must_subscribe: { label: 'Must Subscribe', bucket: 'apply', strength: 2 },
  apply: { label: 'Apply', bucket: 'apply', strength: 1 },
  may_apply: { label: 'May Apply', bucket: 'may', strength: 0 },
  neutral: { label: 'Neutral', bucket: 'may', strength: 0 },
  active_seekers: { label: 'Active Seekers Only', bucket: 'may', strength: 0 },
  avoid: { label: 'Avoid', bucket: 'avoid', strength: 0 },
  under_review: { label: 'Under Review', bucket: 'awaited', strength: 0 },
  awaited: { label: 'Awaited', bucket: 'awaited', strength: 0 },
};

export const VERDICT_BUCKETS = [
  { key: 'apply', label: 'Apply' },
  { key: 'may', label: 'May Apply' },
  { key: 'avoid', label: 'Avoid' },
  { key: 'awaited', label: 'Awaited' },
];

export const consensusBucket = (review) => CONSENSUS[review.consensus].bucket;

// shortLabel is shown on phones, where the full labels don't fit side by side
export const REVIEW_TABS = [
  { key: 'all', label: 'All IPOs', shortLabel: 'All', test: () => true },
  { key: 'mainboard', label: 'Mainboard IPOs', shortLabel: 'Mainboard', test: (review) => review.board === 'mainboard' },
  { key: 'sme', label: 'SME IPOs', shortLabel: 'SME', test: (review) => review.board === 'sme' },
];

export const reviewTabByKey = (key) => REVIEW_TABS.find((tab) => tab.key === key) || REVIEW_TABS[0];

export const PLATFORMS = ['Mainboard', 'BSE SME', 'NSE SME'];

// CM score bands: 51+ must subscribe, 45-50 may subscribe, 40-44 high risk, under 40 avoid
export const cmBand = (score) => {
  if (score === null || score === undefined) return null;
  if (score >= 51) return 'must';
  if (score >= 45) return 'may';
  return score >= 40 ? 'high' : 'avoid';
};

export const CM_BAND_RISK = { must: 'Low Risk', may: 'Moderate Risk', high: 'High Risk', avoid: 'Very High Risk' };

const includesText = (text, needle) => text.toLowerCase().includes(needle);

// Newest advisory update first; reviews updated on the same day keep their data order
export const filterReviews = (
  reviews,
  { tab = 'all', verdict = 'all', company = '', platform = 'all', minScore = '', analyst = '', consensus = 'all' } = {}
) => {
  const companyNeedle = company.trim().toLowerCase();
  const analystNeedle = analyst.trim().toLowerCase();
  const min = minScore === '' ? NaN : Number(minScore);
  return reviews
    .filter(reviewTabByKey(tab).test)
    .filter((review) => verdict === 'all' || consensusBucket(review) === verdict)
    .filter((review) => !companyNeedle || includesText(`${review.company} ${review.sector}`, companyNeedle))
    .filter((review) => platform === 'all' || review.platform === platform)
    .filter((review) => Number.isNaN(min) || (review.cmScore !== null && review.cmScore >= min))
    .filter((review) => !analystNeedle || (review.ratings || []).some((rating) => includesText(rating.analyst, analystNeedle)))
    .filter((review) => consensus === 'all' || review.consensus === consensus)
    .sort((a, b) => b.updatedOn.localeCompare(a.updatedOn));
};

export const countByBucket = (reviews) => {
  const counts = { apply: 0, may: 0, avoid: 0, awaited: 0 };
  reviews.forEach((review) => {
    counts[consensusBucket(review)] += 1;
  });
  return counts;
};

// Strongest consensus first, then the higher CM score
export const pickTopReview = (reviews) =>
  reviews
    .filter((review) => consensusBucket(review) === 'apply' && review.cmScore !== null)
    .reduce((best, review) => {
      if (!best) return review;
      const stronger = CONSENSUS[review.consensus].strength - CONSENSUS[best.consensus].strength;
      return stronger > 0 || (stronger === 0 && review.cmScore > best.cmScore) ? review : best;
    }, null);

export const isUnanimousApply = (review) =>
  Boolean(review.ratings?.length) && review.ratings.every((rating) => rating.tone === 'positive');

// Brokerages with at least one published call, most active first
export const researchCoverage = (reviews, limit = 5) => {
  const byName = new Map();
  reviews.forEach((review) =>
    (review.ratings || []).forEach(({ analyst }) => {
      const name = typeof analyst === 'string' ? analyst.trim() : '';
      if (!name) return;
      byName.set(name, { name, calls: (byName.get(name)?.calls || 0) + 1 });
    })
  );
  const brokers = [...byName.values()].sort((a, b) => b.calls - a.calls || a.name.localeCompare(b.name));
  return {
    brokerCount: brokers.length,
    calls: brokers.reduce((sum, broker) => sum + broker.calls, 0),
    top: brokers.slice(0, limit),
  };
};

export const computeReviewStats = (reviews) => {
  const counts = countByBucket(reviews);
  return {
    coverage: researchCoverage(reviews),
    total: reviews.length,
    sme: reviews.filter((review) => review.board === 'sme').length,
    mainboard: reviews.filter((review) => review.board === 'mainboard').length,
    counts,
    finalized: reviews.length - counts.awaited,
    applyShare: reviews.length ? (counts.apply / reviews.length) * 100 : null,
    topPick: pickTopReview(reviews),
  };
};

const csvCell = (value) => {
  if (value === null || value === undefined) return '';
  const text = String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const REPORT_LABELS = { pdf: 'PDF note', note: 'Research note' };

export const buildReviewsCsv = (reviews) => {
  const header = [
    'Company', 'Sector', 'Board', 'Platform', 'CM Score', 'Broker Ratings', 'Research Note', 'Consensus', 'Report', 'Last Updated',
  ];
  const lines = reviews.map((review) =>
    [
      review.company,
      review.sector,
      review.board === 'sme' ? 'SME' : 'Mainboard',
      review.platform,
      review.cmScore,
      (review.ratings || []).map((rating) => `${rating.analyst}: ${rating.call}`).join('; '),
      [review.note, review.recommendation && `Recommendation: ${review.recommendation}`].filter(Boolean).join(' '),
      CONSENSUS[review.consensus].label,
      REPORT_LABELS[review.report] || 'Pending',
      review.updatedOn,
    ]
      .map(csvCell)
      .join(',')
  );
  return [header.map(csvCell).join(','), ...lines].join('\r\n');
};
